import { Database, Calculator, Shield, TrendingUp, Users } from 'lucide-react'
import { AI_WAGE_PREMIUM_BASELINE, DATA_LAST_UPDATED, DATA_SOURCES, PREMIUM_SOURCE, PREMIUM_SOURCE_URL } from '../utils/constants.ts'

const dataSources = [
  { name: 'PwC 2025 Global AI Jobs Barometer', use: `${AI_WAGE_PREMIUM_BASELINE}% AI wage premium benchmark and industry exposure rates.` },
  { name: 'U.S. Bureau of Labor Statistics (BLS)', use: 'Median base salaries, job growth, and occupational employment data.' },
  { name: 'O*NET', use: 'Repetitive task percentages, skill importance, and role-to-task mapping.' },
  { name: 'WEF Future of Jobs Report 2025', use: 'Automation timelines and workforce transition assumptions.' },
  { name: 'GitHub Copilot, GPT-4, Claude public pricing', use: 'AI tool costs, token prices, and productivity benchmarks.' }
]

export default function MethodologyPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
          Methodology: How We Model the {AI_WAGE_PREMIUM_BASELINE}% AI Wage Premium
        </h1>
        <p className="text-lg text-slate-600">
          Every result in The AI Economy Suite is deterministic. We do not use AI to generate estimates.
          Instead, we combine publicly available labor and AI market data with a transparent formula,
          so you can audit every number. Salary and premium data were last updated in {DATA_LAST_UPDATED}.
        </p>
      </div>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-primary-600" aria-hidden="true" /> Data sources
        </h2>
        <div className="card p-6">
          <ul className="space-y-4">
            {dataSources.map((source, i) => (
              <li key={i} className="flex flex-col sm:flex-row sm:gap-4">
                <span className="font-medium text-slate-900 sm:w-64">{source.name}</span>
                <span className="text-slate-600">{source.use}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary-600" aria-hidden="true" /> Core model
        </h2>
        <div className="card p-6 space-y-4 text-slate-700">
          <p>
            <strong>AI salary gap.</strong> The premium for a role is calculated as
            <code> baseSalary × basePremium × experienceMultiplier × industryMultiplier × aiUsageMultiplier</code>.
            The result is compared against a baseline salary without AI to produce the year-by-year gap.
          </p>
          <p>
            <strong>Skills ROI.</strong> We combine learning time, course cost, and the salary premium for a target role
            to produce a payback period, net present value, and 10-year lifetime value.
          </p>
          <p>
            <strong>Displacement risk.</strong> We combine the O*NET-based repetitive task percentage and the role&apos;s
            AI exposure tier to estimate years until disruption and income at risk.
          </p>
          <p>
            <strong>Business calculators.</strong> Each business calculator uses a static formula, documented inside the tool,
            so you can trace the result from input to output.
          </p>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary-600" aria-hidden="true" /> From the {AI_WAGE_PREMIUM_BASELINE}% aggregate to a role-specific premium
        </h2>
        <div className="card p-6 space-y-4 text-slate-700">
          <p>
            The {AI_WAGE_PREMIUM_BASELINE}% figure is the median AI wage premium reported by the{' '}
            <a href={PREMIUM_SOURCE_URL} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
              {PREMIUM_SOURCE}
            </a>.
            It is a population average across surveyed roles, not a guarantee that every individual will earn {AI_WAGE_PREMIUM_BASELINE}% more.
          </p>
          <p>
            <strong>How we interpolate.</strong> We distribute the aggregate premium across roles based on AI exposure tier
            (Low, Medium, High, Very High), O*NET repetitive-task scores, and BLS salary levels. Roles with more automatable
            tasks and higher base salaries receive a higher modeled premium than roles with less exposure.
          </p>
          <p>
            <strong>Key assumptions.</strong>
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>We assume a linear premium ramp over the selected time horizon (typically 1–5 years).</li>
            <li>Industry and experience multipliers are capped at 1.25× to avoid compounding small effects.</li>
            <li>Skills premiums are additive: they are applied on top of the role&apos;s base premium, not stacked multiplicatively.</li>
            <li>Long-range projections (5+ years) assume constant labor market conditions; actual markets will evolve.</li>
          </ul>
          <p>
            <strong>Confidence and uncertainty.</strong> The {AI_WAGE_PREMIUM_BASELINE}% is a median with real variance by geography,
            company size, and skill level. A software engineer in San Francisco may see a larger premium than a software engineer in
            a small market. Treat our role-specific figures as directional estimates, not precise forecasts.
          </p>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary-600" aria-hidden="true" /> Limitations
        </h2>
        <div className="card p-6 text-slate-700">
          <p>
            Results are estimates, not financial advice. Local wages, tax rules, and AI tool prices change.
            The {AI_WAGE_PREMIUM_BASELINE}% premium is a global average; your local market may differ. Always verify salary and tax
            assumptions with a qualified professional.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Data sources: {DATA_SOURCES.join(', ')}. Last updated: {DATA_LAST_UPDATED}.
          </p>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary-600" aria-hidden="true" /> Annual AI wage premium report
        </h2>
        <div className="card p-6 text-slate-700">
          <p>
            We update our role premiums, skills, and exposure tiers annually based on the latest BLS, O*NET,
            PwC, and WEF releases. The next update is scheduled for Q1 2026.
          </p>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary-600" aria-hidden="true" /> Who built this
        </h2>
        <div className="card p-6 text-slate-700">
          <p>
            The AI Economy Suite is maintained by a small team of economists, engineers, and career strategists.
            Our goal is to make the AI wage premium measurable and actionable for every professional.
          </p>
        </div>
      </section>

      <div className="flex flex-wrap gap-4">
        <a href="/ai-salary-gap" className="btn-primary">Try the AI Salary Gap Calculator</a>
        <a href="/hub" className="btn-secondary">Browse all calculators</a>
      </div>
    </div>
  )
}
